import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useMenuPermission } from '@src/recoil/permission';
import { PRODUCT_KEYS } from '@src/types/productType';

import { CallGetAllProducts } from '@src/library/request/callProduct';
import Spinner from '@components/molecules/Spinner';

const Recommend = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const storeId = searchParams.get('storeID');

  const { menuPermission } = useMenuPermission(storeId);

  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState('');

  const [item, setItem] = useState([]);

  // 권한 체크
  const hasPermission = useMemo(() => {
    if (!menuPermission.isFetch || !menuPermission.data?.[0]) return null; // 아직 모름
    const { permissionType, recommendMenu } = menuPermission.data[0];
    return permissionType === 1 || (permissionType === 2 && recommendMenu === 1);
  }, [menuPermission]);

  const { data: products, refetch } = useQuery([PRODUCT_KEYS.PRODUCT, storeId], {
    cacheTime: 0,
    enabled: hasPermission === true, // 권한 있을 때만 데이터 로드
    queryFn: async () => {
      try {
        setLoading(true);
        const res = await CallGetAllProducts(storeId);
        if (res.data) {
          const currentData = res.data as any;
          if (currentData.products.length === 0) {
            setShow('noneMenu');
          }
          setItem(currentData.recommend.products);
          if (currentData.recommend.products.length === 0) {
            setShow('noneRecommend');
          } else {
            setShow('recommend');
          }
        }

        return res.data as any;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  const clickBannerKakao = () => {
    if (window.navigator.userAgent.indexOf('Cupping-Biz') > -1) {
      // 앱일 때
      window.location.href = 'cuppingbiz://docommand?type=6&value1=https://pf.kakao.com/_TjKnC/chat';
    } else {
      // 웹일 때
      window.open('https://pf.kakao.com/_TjKnC/chat');
    }
  };

  useEffect(() => {
    if (hasPermission === null) {
      setShow('loading');
      return;
    }
    if (!hasPermission) {
      setShow('auth');
    }
    // 권한 있으면 useQuery에서 show 설정
  }, [hasPermission]);

  return (
    <div className="art-product">
      {show === 'loading' && (
        <div className="box-empty">
          <Spinner show={true} />
        </div>
      )}
      {show === 'noneMenu' && (
        <div id="emptyBox" className="box-empty">
          <div className="border-grey" />
          <img
            src="//image.thecupping.co.kr/partners/web/img-empty-data@2x.png"
            width="140"
            height="140"
            className="img-empty"
            alt=""
          />
          <p className="desc-empty">
            등록된 메뉴가 없습니다.
            <br />
            메뉴 등록 후 이용해 주세요.
          </p>
        </div>
      )}
      {show === 'external' && (
        <div id="externalBox" className="box-empty">
          <div className="border-grey" />
          <img
            src="//image.thecupping.co.kr/partners/web/img-empty-data@2x.png"
            width="140"
            height="140"
            className="img-empty"
            alt=""
          />
          <p className="desc-empty">
            위메프오 프로그램 외 주문 접수
            <br />및 POS 프로그램 사용 시<br />
            해당 기능은 지원하지 않습니다.
          </p>
          <p className="desc-empty-info">
            메뉴 수정 요청은
            <button type="button" onClick={clickBannerKakao}>
              [카카오톡 플러스 친구]
            </button>
            를<br />
            통해 요청해 주세요.
          </p>
        </div>
      )}
      {show === 'noneRecommend' && (
        <div id="emptyRecommendBox" className="box-empty">
          <div className="border-grey" />
          <img
            src="//image.thecupping.co.kr/partners/web/img-empty-data@2x.png"
            width="140"
            height="140"
            className="img-empty"
            alt=""
          />
          <p className="desc-empty">설정된 추천 메뉴가 없습니다.</p>
          <p className="desc-empty-info">
            아래 버튼을 클릭하여 메뉴를
            <br />
            설정해 보세요.
          </p>
          <button
            type="button"
            className="btn-round-add"
            onClick={() => {
              navigate(`/product/recommend?storeID=${storeId}`);
            }}
          >
            <span className="ico-plus" />
            추천메뉴 선택
          </button>
        </div>
      )}
      {show === 'recommend' && (
        <div id="recommendBox" className="sec-product">
          <div className="box-recommend">
            <em className="emph-count">
              현재 추천 메뉴 <span className="js-count">{item.length}</span>개
            </em>
            <p className="desc-info">메뉴 이미지가 등록된 메뉴만 추천 메뉴로 설정하실 수 있습니다.</p>
          </div>
          <ul className="list-recommend">
            {item &&
              item.map((v, idx) => {
                return (
                  <li key={`recommned-${idx}`}>
                    <img src={v.imageUrl} width="60" height="60" className="thumb-recommend" alt="" />
                    <div className="info-recommend">
                      <strong className="tit-subject">{v.name}</strong>
                      {v.productDescription && <span className="txt-story">{v.productDescription}</span>}
                    </div>
                  </li>
                );
              })}
          </ul>
          <div className="fixedBtnGroup">
            <button
              type="button"
              id="btnSubmit"
              className="btnDone"
              onClick={() => {
                navigate(`/product/recommend?storeID=${storeId}`);
              }}
            >
              수정
            </button>
          </div>
        </div>
      )}
      {show === 'auth' && (
        <div className="box-empty auth-wrapper">
          <h4 className="screen-out">추천 메뉴</h4>
          <img
            src="//image.thecupping.co.kr/partners/web/img-empty-data@2x.png"
            width="140"
            height="140"
            className="img-empty"
            alt=""
          />
          <p className="desc-empty">추천 메뉴에 대한 권한이 없습니다.</p>
          <p className="desc-empty-info">
            추천 메뉴 수정은
            <button type="button" onClick={clickBannerKakao}>
              [카카오톡 채널]
            </button>
            을<br />
            통해 요청해 주세요.
          </p>
        </div>
      )}
      <Spinner show={loading} />
    </div>
  );
};

export default Recommend;
